import { useState } from "react";
import { toast } from "sonner";
import { Eye, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useApi, errMsg } from "@/lib/api";
import { fmtDate, fmtStamp as stamp } from "@/lib/format";

const day = (d) => `${new Date(`${d}T12:00`).toLocaleDateString("en-US", { weekday: "short" })}, ${fmtDate(d)}`;

function BriefPreview({ p }) {
  const Row = ({ k, v, testId }) => <p className="text-xs text-zinc-300"><span className="text-zinc-500 uppercase tracking-[0.18em] text-[0.6rem] mr-2">{k}</span><span data-testid={testId}>{v}</span></p>;
  return (
    <div className="space-y-3" data-testid="trip-brief-preview">
      <div className="space-y-1 border-b border-white/[0.08] pb-3">
        <Row k="To" v={p.to_name ? `${p.to_name} <${p.to}>` : p.to} testId="trip-brief-preview-to" />
        <Row k="Subject" v={p.subject} testId="trip-brief-preview-subject" />
        {p.attachment && <Row k="Attached" v={p.attachment} testId="trip-brief-preview-attachment" />}
      </div>
      <iframe title="Trip brief email preview" srcDoc={p.html} sandbox="allow-same-origin" className="w-full h-[55vh] bg-[#eeedea] border border-white/10" data-testid="trip-brief-preview-frame" />
    </div>
  );
}

export function TripBriefButton({ t, onSaved }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const b = t.brief;
  if (!b) return null;
  const legs = t.legs.filter((lg) => lg.date === b.date && ["scheduled", "delayed"].includes(lg.status)).length;
  const close = () => { setOpen(false); setPreview(null); };
  const showPreview = async () => {
    setBusy(true);
    try { setPreview((await api.post(`/tracking/${t.id}/brief/preview`, { note })).data); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tracking/${t.id}/brief`, { note });
      toast.success(`Sending the trip brief to ${t.broker_name || "the broker"}`);
      onSaved(data);
      close();
      setNote("");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-1" data-testid="trip-brief">
      <button type="button" className="frame-btn gold" disabled={!b.can_send} onClick={() => setOpen(true)} data-testid="trip-brief-send-btn">
        <Send size={13} /> {b.updated_since && b.open ? "Send updated brief" : b.sent_at ? "Brief sent" : "Send trip brief"} · {day(b.date)}
      </button>
      {!b.open && (
        <span className="text-[0.72rem] text-zinc-300" data-testid="trip-brief-opens-note">Opens {stamp(b.opens_at)}, 24 hours before the first departure</span>
      )}
      {b.sent_at && (
        <span className="text-[0.72rem] text-zinc-300" data-testid="trip-brief-sent-note">
          Sent {stamp(b.sent_at)}{b.sent_by ? ` by ${b.sent_by}` : ""}{b.updated_since ? " · trip details changed since" : ""}
        </span>
      )}
      {b.failed && b.can_send && (
        <span className="text-[0.72rem] text-rose-300" data-testid="trip-brief-failed-note">The last brief email didn't go through. Please send it again.</span>
      )}
      <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className={`bg-[#111319] border-white/10 max-h-[94vh] overflow-y-auto ${preview ? "max-w-3xl" : "max-w-md"}`} data-testid="trip-brief-dialog">
          <DialogHeader>
            <p className="eyebrow">{t.trip_ref} · {day(b.date)}</p>
            <DialogTitle className="font-serif-display text-3xl font-normal text-white">{preview ? "Email preview" : b.updated_since ? "Send updated brief?" : "Send trip brief?"}</DialogTitle>
          </DialogHeader>
          {preview ? <BriefPreview p={preview} /> : (
            <>
              <p className="text-sm text-zinc-300">
                {t.broker_name || "The broker"} gets the night-before brief for the {legs} leg{legs === 1 ? "" : "s"} flying {day(b.date)}: your movement details, departure and arrival FBO, passengers, catering and ground transport
                {t.trip_sheet ? ", with the itinerary attached" : ""}. It can only be sent again if the trip details change.
              </p>
              <div>
                <label htmlFor="brief-note" className="text-[0.6rem] tracking-[0.22em] uppercase text-zinc-400">Movement details (required)</label>
                <Textarea id="brief-note" className="bg-[#0d0f14] border-white/10 mt-2 min-h-[110px]" maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Crew shows 8:30 AM. Wheels up 9:30 AM from Signature TEB. Car arrives 9:00 AM; gate code 4412." data-testid="trip-brief-note-input" />
              </div>
            </>
          )}
          <div className="flex flex-wrap justify-end gap-3 mt-2">
            {preview
              ? <button type="button" className="frame-btn" onClick={() => setPreview(null)} data-testid="trip-brief-edit-btn">Edit details</button>
              : <>
                  <button type="button" className="frame-btn" onClick={close} data-testid="trip-brief-cancel-btn">Back</button>
                  <button type="button" className="frame-btn" disabled={busy} onClick={showPreview} data-testid="trip-brief-preview-btn"><Eye size={13} /> Preview email</button>
                </>}
            <button type="button" className="frame-btn gold" disabled={busy || !note.trim()} onClick={send} data-testid="trip-brief-confirm-btn">{busy ? "Please wait…" : "Send brief"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
